import {inject, Injectable, resource, signal} from '@angular/core';
import {Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {FactureExterneDTO} from '../../dtos/facturation/fournisseurs/factures/facture-externe-dto';
import {FactureExterneFilters} from '../../dtos/facturation/fournisseurs/factures/facture-externe-filter-dto';
import {CegidFiltersDTO} from '../../dtos/facturation/fournisseurs/cegid/cegid-filters-dto';
import {CegidExportResultDTO} from '../../dtos/facturation/fournisseurs/cegid/cegid-export-result-dto';
import {GraphQLService} from '../technique/graphql.service';
import {Auth2Service} from '../security/auth2.service';
import {HttpService} from '../technique/http.service';
import {ResponseWrapper} from '../../suppliers/wrappers/response-wrapper';

@Injectable({providedIn: 'root'})
export class FactureExterneService {

  private readonly graphQlSvc = inject(GraphQLService);
  private readonly auth2Svc = inject(Auth2Service);
  private readonly httpSvc = inject(HttpService)

  exportCegidFactureAvoir = (filters: CegidFiltersDTO = {}): Observable<CegidExportResultDTO> =>
    this.httpSvc.post('dolrest/api/export/cegid/facture-avoir', filters).pipe(
      map((response: ResponseWrapper<CegidExportResultDTO>) => response.one)
    );

  validerEcart = (id: number): Observable<ResponseWrapper<boolean>> =>
    this.httpSvc.post(`dolrest/facture-externe/rapprochement/${id}/valider-ecart`, {});

  valider = (id: number): Observable<ResponseWrapper<boolean>> =>
    this.httpSvc.post(`dolrest/facture-externe/${id}/valider`, {});

  abandonner = (id: number): Observable<ResponseWrapper<boolean>> =>
    this.httpSvc.post(`dolrest/facture-externe/${id}/abandonner`, {});

  refuser = (id: number): Observable<ResponseWrapper<boolean>> =>
    this.httpSvc.post(`dolrest/facture-externe/${id}/refuser`, {});

  findById = (id: number): Observable<FactureExterneDTO> =>
    this.graphQlSvc.sendQuery(`
      {
        allFactureExterne(filters: { idList: [${id}] }) {
          id,
          source,
          externalReference,
          numero,
          dateFacture,
          dateSaisieComptable,
          fournisseurCode,
          siteExterneId,
          siteExterneLibelle,
          montantHT,
          montantTTC,
          tva1Montant, tva1Taux,
          tva2Montant, tva2Taux,
          tva3Montant, tva3Taux,
          tva4Montant, tva4Taux,
          tva5Montant, tva5Taux,
          documentNom,
          url,
          dateCreationSource,
          dateRapprochement,
          statut,
          rapprochementStatut,
          avoirBloquant,
          rapprochementList {
            id,
            factureExterneId,
            numeroBonLivraison,
            bonReceptionId,
            dateReception,
            rapproche,
            factureCommandeFournisseurId,
            factureCommandeFournisseurNumero,
            montantHT,
            montantTTC,
            tva1Montant, tva1Taux,
            tva2Montant, tva2Taux,
            tva3Montant, tva3Taux,
            tva4Montant, tva4Taux,
            tva5Montant, tva5Taux
          }
        }
      }
    `).pipe(
      take(1),
      map((response: any) => (response?.allFactureExterne?.[0] ?? null) as FactureExterneDTO)
    );

  readonly filtersSignal = signal<FactureExterneFilters>({ page: 0, limit: 20 });

  readonly factureExterneResource = resource<FactureExterneDTO[], FactureExterneFilters>({
    params: () => this.filtersSignal(),
    loader: ({ params: filters }) => this.findAll(filters)
  });

  private findAll = async (filters: FactureExterneFilters): Promise<FactureExterneDTO[]> => {
    const parts: string[] = [];
    if (filters.numero) parts.push(`numero: "${filters.numero}"`);
    if (filters.numeroBonLivraison) parts.push(`numeroBonLivraison: "${filters.numeroBonLivraison}"`);
    if (filters.statut) parts.push(`statut: "${filters.statut}"`);
    if (filters.rapprochementStatut) parts.push(`rapprochementStatut: "${filters.rapprochementStatut}"`);
    if (filters.dateReceptionDebut) parts.push(`dateReceptionDebut: "${filters.dateReceptionDebut}"`);
    if (filters.dateReceptionFin) parts.push(`dateReceptionFin: "${filters.dateReceptionFin}"`);
    if (filters.factureIdList?.length) parts.push(`idList: [${filters.factureIdList.join(', ')}]`);
    if (filters.avoirIdList?.length) parts.push(`avoirIdList: [${filters.avoirIdList.join(', ')}]`);
    if (filters.page != null) parts.push(`page: ${filters.page}`);
    if (filters.limit != null) parts.push(`limit: ${filters.limit}`);
    const filtersGql = parts.length ? `(filters: { ${parts.join(', ')} })` : '';

    const response: any = await this.graphQlSvc.sendQuery(`
      {
        allFactureExterne${filtersGql} {
          id,
          numero,
          statut,
          rapprochementStatut,
          dateFacture,
          dateRapprochement,
          fournisseurCode,
          siteExterneLibelle,
          avoirBloquant,
          montantHT,
          montantTTC,
          rapprochementList {
            numeroBonLivraison
          }
        }
      }
    `).pipe(take(1)).toPromise();
    return (response?.allFactureExterne ?? []) as FactureExterneDTO[];
  };
}
